// src/pages/ChangePassword.js
import React, { useState } from "react";
import styled from "styled-components";
import {
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import { auth } from "./firebase/firebaseConfig";
import { Lock } from "lucide-react";
import { toast } from "react-toastify";
export const getAuthErrorMessage = (code) => {
  switch (code) {
    case "auth/wrong-password":
      return "❌ Current password is incorrect.";
    case "auth/weak-password":
      return "⚠️ New password is too weak (min 6 characters).";
    case "auth/invalid-credential":
      return "❌ Invalid credentials. Please try again.";
    case "auth/too-many-requests":
      return "🚫 Too many failed attempts. Try again later.";
    case "auth/network-request-failed":
      return "🌐 Network error. Check your internet connection.";
    case "auth/requires-recent-login":
      return "🔒 Please log in again before changing your password.";
    default:
      return "⚠️ Something went wrong. Please try again.";
  }
};

const Container = styled.div`
  max-width: 500px;
  margin: 50px auto;
  padding: 2rem;
  background: #1e1e2f;
  border-radius: 12px;
  color: white;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
`;

const Title = styled.h2`
  text-align: center;
  margin-bottom: 2rem;
  color: #00d4ff;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;

const Label = styled.label`
  font-weight: 500;
`;

const Input = styled.input`
  padding: 0.9rem;
  border: none;
  border-radius: 8px;
  background: #2a2a3d;
  color: white;
  width: 100%;
`;

const Button = styled.button`
  padding: 0.9rem;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, #ff7a18, #ff0066);
  color: white;
  font-weight: bold;
  cursor: pointer;
  transition: 0.2s;

  &:hover {
    transform: translateY(-2px);
    opacity: 0.95;
  }
`;

export default function ChangePassword() {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (!oldPassword || !newPassword || !confirmPassword) {
      toast.error("⚠️ Please fill all fields");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("⚠️ Passwords do not match");
      return;
    }

    try {
      const user = auth.currentUser;

      // ✅ Re-authenticate
      const credential = EmailAuthProvider.credential(user.email, oldPassword);
      await reauthenticateWithCredential(user, credential);

      // ✅ Update password
      await updatePassword(user, newPassword);

      toast.success("✅ Password updated successfully!");
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      console.error("❌ Error changing password:", err);
      toast.error(getAuthErrorMessage(err.code));
    }
  };

  return (
    <Container>
      <Title>
        <Lock size={22} /> Change Password
      </Title>
      <Form onSubmit={handleChangePassword}>
        <div>
          <Label>Current Password</Label>
          <Input
            type="password"
            placeholder="Enter current password"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
          />
        </div>

        <div>
          <Label>New Password</Label>
          <Input
            type="password"
            placeholder="Enter new password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </div>

        <div>
          <Label>Confirm New Password</Label>
          <Input
            type="password"
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </div>

        <Button type="submit">Update Password</Button>
      </Form>
    </Container>
  );
}
